/* @flow */
import Model from "nexia_mobile/framework/model";
import Platform from "nexia_mobile/platform";
import Environment from "nexia_mobile/environment";
import Url from "nexia_mobile/url";

const KEYS = {
  AUTH_TYPE: "auth_type",
  PIN_NUMBER: "pin_number",
  AUTH_ATTEMPTS: "auth_attempts",
  AUTH_ATTEMPT_AT: "auth_attempt_at",
  MOBILE_ID: "mobile_id",
  API_KEY: "api_key",
  HOST: "host",
  DEFAULT_LOCATION_ID: "default_location_id",
  HIGH_RES_VIDEO: "high_res_video",
  PUSH_NOTIFICATION_TOKEN: "push_notification_token",
  NOTIFICATION_SOUND_ENABLED: "notification_sound_enabled",
  DEVICE_SORT_ORDER: "device_sort_order",
  SHOW_NEWS: "show_news",
  VOGNITION: "vognition",
  VOGNITION_ON_STARTUP: "vognition_on_startup",
  DEBUG: "debug",
  DEBUG_VIDEO: "debug_video",
  DEBUG_STREAMING: "debug_streaming",
  SHOW_POLLING: "show_polling",
  FAST_POLLING: "fast_polling",
  DASHBOARD_AUTOMATIONS: "dashboard_automations",
  THERMOSTAT_SCHEDULING_ENABLED: "thermostat_scheduling_enabled",
  EVENT_STREAMING_ENABLED: "event_streaming_enabled",
  MULTI_VIDEO_ENABLED: "multi_video_enabled",
  PHONE_AUTH_PIN_ENABLED: "phone_auth_pin_enabled",
  DRAW_UPDATES: "draw_updates",
  DARK_MODE: "dark_mode",
  HAS_SEEN_THERMOSTAT_SCHEDULING_INSTRUCTIONS:
    "has_seen_thermostat_scheduling_instructions",
  HANDEDNESS: "handedness",
  LOCALE: "locale",
};

export const AUTH_TYPES = {
  PIN: "pin",
  FINGERPRINT: "fingerprint",
  NONE: "none",
  UNINITIALIZED: "undefined",
};

const AUTH_LOCKOUT_ATTEMPTS = 5;
export const AUTH_LOCKOUT_MINUTES = 5;
const isCommercial = Environment.isCommercial();

class LocalSettings extends Model {
  initialize() {
    this.listenTo(Platform, Platform.Event.NOTIFICATION_TOKEN, (event) => {
      $.ajax({
        type: "POST",
        url: `${this.getHost()}/mobile/register_for_push_notifications`,
        data: {
          /* eslint-disable camelcase */
          push_notification_token: event.token,
          /* eslint-enable camelcase */
        },
      });
    });
  }

  // This is called when the script interface is ready to update platform data with new patterns
  ensureBackwardsCompatibility() {
    {
      // 4.11 -> 4.12 changed "PIN" to "PIN_NUMBER"
      const pin = this.getValue("pin");
      if (pin != null) {
        this.setValue(KEYS.PIN_NUMBER, pin);
        this.clearValue("pin");
      }
    }

    {
      // 4.11 -> 4.12 changed authorization patterns to support fingerprints
      //   - pin_required now maps to an auth_type
      const pinRequired = this.getBooleanValue("pin_required", "default value");
      switch (pinRequired) {
        case true: {
          // An upgrading user who chose to require PIN is migrated to the PIN auth type
          this.setAuthToPin();
          break;
        }
        case false: {
          // An upgrading user who chose to disable PIN will continue to have to authorization
          this.setAuthToNone();
          break;
        }
        default: {
          if (this.getValue(KEYS.AUTH_TYPE)) {
            // A fully migrated user doesn't need further work
          } else if (this.hasPin()) {
            // An upgrading user who never changed the settings is migrated to PIN auth as well
            this.setAuthToPin();
          } else if (!this.getValue(KEYS.AUTH_TYPE)) {
            // A user who has a fresh install is defaulted to PIN or fingerprint if available
            this.setAuthToPin();
            Platform.isFingerprintAuthAvailable()
              .then((available) => {
                if (available) {
                  this.setAuthToFingerprint();
                }
              })
              .catch(() => {
                // ignore platform bridge error
              });
          }
        }
      }
      this.clearValue("pin_required");
    }
  }

  authenticate(username, password, params) {
    let auth = new Model({
      /* eslint-disable camelcase */
      login: username,
      password,
      device_uuid: Platform.deviceUUID,
      is_commercial: isCommercial,
      ...params,
      /* eslint-enable camelcase */
    });
    const host = this.getHost();
    auth.url = `${host}/mobile/authenticate`;
    return auth.save().catch((xhr) => {
      const { message } = xhr.responseJSON.error;
      throw message || "An unknown error occured.";
    });
  }

  setMobileIdAndApiKey(mobileId, apiKey) {
    this.setValue(KEYS.MOBILE_ID, mobileId);
    return this.setValue(KEYS.API_KEY, apiKey);
  }

  setLocale(locale) {
    return this.setValue(KEYS.LOCALE, locale || "en_us");
  }

  getLocale() {
    return this.getValue(KEYS.LOCALE);
  }

  getMobileId() {
    return this.getValue(KEYS.MOBILE_ID);
  }

  getApiKey() {
    return this.getValue(KEYS.API_KEY);
  }

  getHost() {
    let host = this.getValue(KEYS.HOST);
    return host == null ? Environment.defaultHostForEnvironment() : host;
  }

  setHost(url) {
    return this.setValue(KEYS.HOST, Url.getHostAddress(url));
  }

  isPointedToProductionHost() {
    return this.getHost() === Environment.PRODUCTION_HOST;
  }

  canSkipLogin() {
    return this.isActivated() && !this.needsAuthentication();
  }

  isActivated() {
    return (
      this.getValue(KEYS.MOBILE_ID) != null &&
      this.getValue(KEYS.API_KEY) != null
    );
  }

  getCurrentAuth() {
    if (!Platform.isMobilePlatform()) {
      return AUTH_TYPES.NONE;
    }
    return this.getValue(KEYS.AUTH_TYPE) || AUTH_TYPES.UNINITIALIZED;
  }

  setAuthToPin() {
    this.setValue(KEYS.AUTH_TYPE, AUTH_TYPES.PIN);
  }
  setAuthToFingerprint() {
    this.setValue(KEYS.AUTH_TYPE, AUTH_TYPES.FINGERPRINT);
  }
  setAuthToNone() {
    this.setValue(KEYS.AUTH_TYPE, AUTH_TYPES.NONE);
  }

  needsAuthentication() {
    const currentAuth = this.getCurrentAuth();
    return !(
      currentAuth === AUTH_TYPES.NONE ||
      currentAuth === AUTH_TYPES.UNINITIALIZED
    );
  }

  hasPin() {
    return this.getValue(KEYS.PIN_NUMBER) != null;
  }

  pinLength() {
    return (this.getValue(KEYS.PIN_NUMBER) || "").length;
  }

  needsPin() {
    return Platform.isMobilePlatform() && !this.hasPin();
  }

  isPinValid(value) {
    return value === this.getValue(KEYS.PIN_NUMBER);
  }

  setEnabledByAuthPin(bool) {
    return this.setValue(KEYS.PHONE_AUTH_PIN_ENABLED, bool);
  }

  isEnabledByAuthPin() {
    return this.getValue(KEYS.PHONE_AUTH_PIN_ENABLED);
  }
  saveNewPin(value) {
    this.setValue(KEYS.PIN_NUMBER, value);
  }

  clearPin() {
    this.clearValue(KEYS.PIN_NUMBER);
    this.clearValue(KEYS.AUTH_ATTEMPTS);
  }

  recordFailedAuthAttempt() {
    let attempts = parseInt(this.getValue(KEYS.AUTH_ATTEMPTS)) || 0;
    attempts++;
    this.setValue(KEYS.AUTH_ATTEMPTS, attempts);
    this.setValue(KEYS.AUTH_ATTEMPT_AT, Date.now().toString());
  }

  isAuthAttemptLockedOut() {
    const lastAttempt = parseInt(this.getValue(KEYS.AUTH_ATTEMPT_AT)) || 0;
    const timeSinceLastAttempt = Date.now() - lastAttempt;

    if (timeSinceLastAttempt < AUTH_LOCKOUT_MINUTES * 60 * 1000) {
      const numberOfAttempts = parseInt(this.getValue(KEYS.AUTH_ATTEMPTS)) || 0;
      if (numberOfAttempts >= AUTH_LOCKOUT_ATTEMPTS) {
        return true;
      }
    }
    return false;
  }

  clearFailedAuthAttempts() {
    this.setValue(KEYS.AUTH_ATTEMPTS, 0);
    this.setValue(KEYS.AUTH_ATTEMPT_AT, 0);
  }

  publish() {
    $.ajax({
      type: "PUT",
      url: `${this.getHost()}/mobile/settings`,
      data: {
        geofencing_enabled: false, // eslint-disable-line camelcase
      },
    });
  }

  setShowNews(yesno) {
    return this.setValue(KEYS.SHOW_NEWS, yesno);
  }
  getShowNews() {
    return this.getBooleanValue(KEYS.SHOW_NEWS, true);
  }

  setNotificationSoundEnabled(yesno) {
    return this.setValue(KEYS.NOTIFICATION_SOUND_ENABLED, yesno);
  }
  getNotificationSoundEnabled() {
    return this.getBooleanValue(KEYS.NOTIFICATION_SOUND_ENABLED, true);
  }

  setDefaultLocationId(id) {
    return this.setValue(KEYS.DEFAULT_LOCATION_ID, id);
  }
  getDefaultLocationId() {
    return this.getValue(KEYS.DEFAULT_LOCATION_ID);
  }

  setShowVognition(yesno) {
    return this.setValue(KEYS.VOGNITION, yesno);
  }
  getShowVognition() {
    return (
      Platform.isVognitionSupported() &&
      this.getBooleanValue(KEYS.VOGNITION, true) &&
      this.isPointedToProductionHost() &&
      !isCommercial
    );
  }

  setShowVognitionOnStartup(yesno) {
    return this.setValue(KEYS.VOGNITION_ON_STARTUP, yesno);
  }

  getShowVognitionOnStartup() {
    return (
      Platform.isVognitionSupported() &&
      this.getBooleanValue(KEYS.VOGNITION_ON_STARTUP, false) &&
      this.isPointedToProductionHost() &&
      !isCommercial
    );
  }

  setDebug(yesno) {
    return this.setValue(KEYS.DEBUG, yesno);
  }
  getDebug() {
    return this.getBooleanValue(KEYS.DEBUG);
  }

  setShowPolling(bool) {
    return this.setValue(KEYS.SHOW_POLLING, bool);
  }
  getShowPolling() {
    return this.getBooleanValue(KEYS.SHOW_POLLING);
  }

  setFastPolling(bool) {
    return this.setValue(KEYS.FAST_POLLING, bool);
  }
  getFastPolling() {
    return this.getBooleanValue(KEYS.FAST_POLLING);
  }

  setDebugVideo(bool) {
    return this.setValue(KEYS.DEBUG_VIDEO, bool);
  }
  getDebugVideo() {
    return this.getBooleanValue(KEYS.DEBUG_VIDEO);
  }

  getDeviceSortOrder() {
    let order = this.getValue(KEYS.DEVICE_SORT_ORDER);
    return order || "default";
  }
  setDeviceSortOrder(newOrder) {
    return this.setValue(KEYS.DEVICE_SORT_ORDER, newOrder);
  }

  setThermostatSchedulingEnabled(bool) {
    this.setValue(KEYS.THERMOSTAT_SCHEDULING_ENABLED, bool);
  }

  getThermostatSchedulingEnabled() {
    return isCommercial
      ? true
      : this.getBooleanValue(KEYS.THERMOSTAT_SCHEDULING_ENABLED, false);
  }

  setEventStreamingEnabled(bool) {
    this.setValue(KEYS.EVENT_STREAMING_ENABLED, bool);
  }

  getEventStreamingEnabled() {
    return this.getBooleanValue(KEYS.EVENT_STREAMING_ENABLED, false);
  }

  setMultiVideoEnabled(bool) {
    this.setValue(KEYS.MULTI_VIDEO_ENABLED, bool);
  }

  getMultiVideoEnabled() {
    return this.getBooleanValue(KEYS.MULTI_VIDEO_ENABLED, false);
  }

  setDrawUpdates(bool) {
    this.setValue(KEYS.DRAW_UPDATES, bool);
  }

  getDrawUpdates() {
    return this.getBooleanValue(KEYS.DRAW_UPDATES, false);
  }

  setDarkMode(bool) {
    this.setValue(KEYS.DARK_MODE, bool);
  }

  getDarkMode() {
    return this.getBooleanValue(KEYS.DARK_MODE, false);
  }

  setHasSeenThermostatSchedulingInstructions(bool) {
    this.setValue(KEYS.HAS_SEEN_THERMOSTAT_SCHEDULING_INSTRUCTIONS, bool);
  }

  getHasSeenThermostatSchedulingInstructions() {
    return this.getBooleanValue(
      KEYS.HAS_SEEN_THERMOSTAT_SCHEDULING_INSTRUCTIONS,
      false
    );
  }

  getHandedness() {
    return this.getValue(KEYS.HANDEDNESS) || "right";
  }

  setHandedness(newValue: "left" | "right") {
    return this.setValue(KEYS.HANDEDNESS, newValue);
  }

  setDebugStreaming(bool) {
    return this.setValue(KEYS.DEBUG_STREAMING, bool);
  }

  getDebugStreaming() {
    return this.getBooleanValue(KEYS.DEBUG_STREAMING);
  }

  getDeviceName() {
    return Platform.deviceName;
  }

  obtainPushNotificationToken() {
    return Platform.obtainPushNotificationToken();
  }

  setAutomationShownOnDashboard(automationId, bool) {
    this.setValue(`${KEYS.DASHBOARD_AUTOMATIONS}_${automationId}`, bool);
  }

  getAutomationShownOnDashboard(automationId) {
    return this.getBooleanValue(
      `${KEYS.DASHBOARD_AUTOMATIONS}_${automationId}`,
      false
    );
  }

  getTrackedSettings() {
    return {
      /* eslint-disable camelcase */
      dark_mode: this.getDarkMode(),
      multi_video_enabled: this.getMultiVideoEnabled(),
      /* eslint-disable camelcase */
    };
  }

  getValue(key) {
    let val = Platform.getStoredValue(key);
    if (val === "undefined") {
      val = undefined;
    }
    if (val === "null") {
      val = null;
    }
    return val;
  }

  getBooleanValue(key, defaultValue) {
    const result = Platform.getStoredValue(key);
    if (_.isBoolean(result)) {
      return result;
    }
    if (_.isString(result)) {
      return result.toLowerCase() === "true";
    } else {
      return defaultValue;
    }
  }

  setValue(key, value) {
    Platform.setStoredValue(key, value.toString());
    this.trigger("settings_changed");
  }

  clearValue(key) {
    Platform.clearStoredValue(key);
  }

  clear() {
    const host = this.getValue(KEYS.HOST);
    Platform.clearSessionCache();
    Platform.clearStoredValues();
    if (host) {
      this.setValue(KEYS.HOST, host);
    }
  }
}

if (LocalSettings.singleton == null) {
  LocalSettings.singleton = new LocalSettings();
}
export default LocalSettings.singleton;



// WEBPACK FOOTER //
// ./src/models/local_settings.js